import React from 'react';
import Spinner from './Spinner';
import './SSOButtons.css';

interface SSOButtonProps {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Web equivalents of `apps/WunderGuide/components/auth/SSOButtons.tsx`. 1:1 visual
 * parity — same colors, height (50px), radius (8px), icon sizing.
 */

function GoogleLogo({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AppleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden fill="#fff">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.823.99-2.18 1.74-3.296 1.65-.143-1.13.43-2.31 1.124-3.04.798-.85 2.17-1.5 3.349-1.69zm4.137 17.09c-.557 1.27-.83 1.85-1.546 2.97-1.005 1.55-2.42 3.49-4.179 3.5-1.563.02-1.965-1.02-4.084-1-2.119.01-2.563 1.02-4.127 1-1.756-.01-3.094-1.75-4.099-3.31C-.001 19.32-.279 14.69 1.394 12.31c1.187-1.7 3.057-2.69 4.815-2.69 1.79 0 2.92.99 4.395.99 1.435 0 2.31-.99 4.378-.99 1.566 0 3.224.85 4.405 2.32-3.873 2.13-3.244 7.66-1.885 8.58z" />
    </svg>
  );
}

export function GoogleSignInButton({
  onClick,
  loading,
  disabled,
}: SSOButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type="button"
      className={`wg-sso wg-sso--google ${isDisabled ? 'wg-sso--disabled' : ''}`.trim()}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
    >
      {loading ? (
        <Spinner size="small" color="#4285F4" />
      ) : (
        <>
          <span className="wg-sso__icon">
            <GoogleLogo size={20} />
          </span>
          <span className="wg-sso__label wg-sso__label--google">
            Continue with Google
          </span>
        </>
      )}
    </button>
  );
}

export function AppleSignInButton({
  onClick,
  loading,
  disabled,
}: SSOButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type="button"
      className={`wg-sso wg-sso--apple ${isDisabled ? 'wg-sso--disabled' : ''}`.trim()}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
    >
      {loading ? (
        <Spinner size="small" color="#FFFFFF" />
      ) : (
        <>
          <span className="wg-sso__icon wg-sso__icon--apple">
            <AppleLogo size={18} />
          </span>
          <span className="wg-sso__label wg-sso__label--apple">
            Continue with Apple
          </span>
        </>
      )}
    </button>
  );
}

export function SSODivider() {
  return (
    <div className="wg-sso-divider">
      <span className="wg-sso-divider__line" />
      <span className="wg-sso-divider__text">or</span>
      <span className="wg-sso-divider__line" />
    </div>
  );
}
